import { Message } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import type { TFunction } from 'i18next';

import { formatMaasFormError } from '@/delivery-type-plugin/capabilities/maas-init';
import type { ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import {
  validateEmbeddedNodesSystemDiskForSubmit,
  validateMaasGroupedResourcesForSubmit,
  validateMaasRunModeTopology,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/submitValidation';
import type { PipelineContext } from '@/delivery-type-plugin/core/types';
import { formToPayload } from '@/delivery-type-plugin/plugins/ModelInstance/headless/payload';
import i18n from '@/i18n';

import { SubmitValidationError } from '../../_shared/common/submit/submitValidation';
import type { TcPluginSpec } from './schema';
import { getTrainingClusterSubmitError, readTrainingClusterFormValues } from './trainingClusterSubmitValidation';

function throwFromMaasSubmitError(error: { key: string; fieldKey?: string; params?: Record<string, string | number> }): never {
  const message = formatMaasFormError(i18n.t.bind(i18n), error.key, error.fieldKey, error.params);
  throw new SubmitValidationError(message);
}

function assertTrainingClusterSubmitReadySync(
  values: ModelInstanceFormValues,
  spec: ReturnType<typeof formToPayload>,
  options?: { skipTenantAndNameValidation?: boolean },
): void {
  if (!options?.skipTenantAndNameValidation) {
    if (!values.workspace?.trim()) {
      throwFromMaasSubmitError({ key: 'common:validate.required.select', fieldKey: 'common:tenant.space' });
    }
    if (!values.alias?.trim()) {
      throwFromMaasSubmitError({ key: 'common:validate.required', fieldKey: 'common:name' });
    }
  }
  if (!values.runtime?.type) {
    throwFromMaasSubmitError({ key: 'common:validate.required.select', fieldKey: 'data-center:maas.runtime.type' });
  }
  const topologyError = validateMaasRunModeTopology({
    runMode: values.runMode,
    headGroupSpec: spec.headGroupSpec,
    workerGroupSpecs: spec.workerGroupSpecs,
  });
  if (topologyError) throwFromMaasSubmitError(topologyError);

  const systemDiskError = validateEmbeddedNodesSystemDiskForSubmit(values);
  if (systemDiskError) throwFromMaasSubmitError(systemDiskError);

  const resourceError = validateMaasGroupedResourcesForSubmit({
    headGroupSpec: spec.headGroupSpec,
    workerGroupSpecs: spec.workerGroupSpecs,
  });
  if (resourceError) throwFromMaasSubmitError(resourceError);
}

/** SKU / 下单 pipeline：同步拓扑与资源校验（不含异步 GPU） */
export function assertTrainingClusterSubmitReady(spec: TcPluginSpec, _ctx: PipelineContext): void {
  const values = spec as ModelInstanceFormValues;
  const miSpec = formToPayload(values, { layout: 'flatGroups' });
  assertTrainingClusterSubmitReadySync(values, miSpec);
}

/** 表单级异步校验：SKU 离步 / 提交 / 下单 */
export async function assertTrainingClusterSubmitReadyOnForm(
  form: FormInstance,
  t: TFunction,
  options?: { skipTenantAndNameValidation?: boolean },
): Promise<void> {
  const values = readTrainingClusterFormValues(form);
  const spec = formToPayload(values, { layout: 'flatGroups' });
  const sharedEnabled = form.getFieldValue('trainingSharedStorageEnabled') === true;
  const submitError = await getTrainingClusterSubmitError(values, spec, {
    skipSharedStorageValidation: !sharedEnabled,
    runtimeTypeOnly: true,
    skipTenantAndNameValidation: options?.skipTenantAndNameValidation,
  });
  if (!submitError) return;
  const message = formatMaasFormError(t, submitError.key, submitError.fieldKey, submitError.params);
  Message.error(message);
  throw Object.assign(new Error(message), { alreadyNotified: true });
}
